import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  OutreachAnalysis,
  OutreachAnalysisFinding,
  OutreachRail,
  OutreachRailSnapshot,
} from "../../../../types/OutreachMetrics";
import {
  listOutreachAnalyses,
  listOutreachRailSnapshots,
  updateOutreachAnalysisFinding,
} from "../../../../database/outreach/outreachApi";

// The judgement half of the screen, loaded once for the whole tab.
//
// This used to live inside the analysis panel, which meant the work queue could
// not see the findings and the capacity panel fetched the same rail documents a
// second time. One hook, one fetch, three readers: the verdict card, the work
// queue and the rail table.
//
// It deliberately does NOT depend on leads. The default view has to paint while
// the lead list is still in flight.

const ANALYSIS_DAYS = 30;
const RAIL_DAYS = 14;

// Named in the empty state so a reader with no data knows what has to run.
export const ANALYSIS_ROUTINE = "the daily outreach analysis routine";

// Worst first, so the rail that needs a person is the first row read.
const STATUS_ORDER = [
  "dead",
  "silent",
  "blocked",
  "under",
  "disarmed",
  "ok",
  "manual",
];

export const PRIORITY_RANK: Record<string, number> = { P0: 0, P1: 1, P2: 2 };

export const isClosed = (finding: OutreachAnalysisFinding) =>
  finding.status === "done" || finding.status === "dismissed";

const statusRank = (status: string): number => {
  const i = STATUS_ORDER.indexOf(status);
  return i < 0 ? STATUS_ORDER.length : i;
};

// When each finding id was first seen, and when it was first closed, across the
// analyses actually listed. The routine tracks no state of its own, so this is
// the only place "open since" can come from, and it is honest about its window:
// an issue older than the listed days reads as first seen on the oldest day
// loaded.
export type FindingHistory = { firstSeen: string; firstClosed: string | null };

export const historyOf = (analyses: OutreachAnalysis[]) => {
  const history = new Map<string, FindingHistory>();
  const oldestFirst = [...analyses].sort((a, b) => a.date.localeCompare(b.date));
  for (const day of oldestFirst) {
    for (const finding of day.findings ?? []) {
      const seen = history.get(finding.id);
      if (!seen) {
        history.set(finding.id, {
          firstSeen: day.date,
          firstClosed: isClosed(finding) ? day.date : null,
        });
      } else if (!seen.firstClosed && isClosed(finding)) {
        seen.firstClosed = day.date;
      }
    }
  }
  return history;
};

export type AnalysisData = {
  analyses: OutreachAnalysis[] | null;
  snapshots: OutreachRailSnapshot[];
  current: OutreachAnalysis | null;
  error: string | null;
  actionError: string | null;
  saving: string | null;
  selectedDate: string;
  setSelectedDate: (date: string) => void;
  history: Map<string, FindingHistory>;
  // Rails for the day on screen, worst status first.
  rails: OutreachRail[];
  railDay: OutreachRailSnapshot | null;
  // False when the rail numbers come from a different day than the analysis, in
  // which case the analysis notes must not be printed beside them.
  railDayMatches: boolean;
  // The analysis on screen is the newest one. Status writes only carry forward
  // from the newest, so an older day takes no clicks.
  isLatestDay: boolean;
  latestDate: string | null;
  openFindings: OutreachAnalysisFinding[];
  // Done and dismissed, so a finding closed by mistake can be reopened.
  closedFindings: OutreachAnalysisFinding[];
  openP0: number;
  setStatus: (
    finding: OutreachAnalysisFinding,
    status: string
  ) => Promise<void>;
  reload: () => void;
};

export const useOutreachAnalysis = (accountId: string): AnalysisData => {
  const [analyses, setAnalyses] = useState<OutreachAnalysis[] | null>(null);
  const [snapshots, setSnapshots] = useState<OutreachRailSnapshot[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [saving, setSaving] = useState<string | null>(null);

  // Every load and every status write is stamped with the generation that
  // started it. Switching account bumps the counter, so a fetch still in flight
  // for the previous account cannot paint its rows here, and a click cannot
  // write a date that belongs to an account the screen has left.
  const generationRef = useRef(0);

  const load = useCallback(async () => {
    const generation = ++generationRef.current;
    setError(null);
    setAnalyses(null);
    // Clear the rails too. railDay falls back to snapshots[0], so leaving them
    // would paint the previous account's capacity numbers under the new one.
    setSnapshots([]);
    if (!accountId) {
      setAnalyses([]);
      setSnapshots([]);
      return;
    }
    try {
      const [days, rails] = await Promise.all([
        listOutreachAnalyses(accountId, ANALYSIS_DAYS),
        // The rail table is additive: no snapshot must not take the findings
        // down with it.
        listOutreachRailSnapshots(accountId, RAIL_DAYS).catch(
          () => [] as OutreachRailSnapshot[]
        ),
      ]);
      if (generationRef.current !== generation) return;
      setAnalyses(days);
      setSnapshots(rails);
      setSelectedDate(days.length ? days[0].date : "");
    } catch (e: any) {
      if (generationRef.current !== generation) return;
      setError(e?.message || "Could not load the daily analysis");
    }
  }, [accountId]);

  useEffect(() => {
    load();
  }, [load]);

  const current = useMemo(() => {
    if (!analyses || !analyses.length) return null;
    return analyses.find((a) => a.date === selectedDate) ?? analyses[0];
  }, [analyses, selectedDate]);

  const history = useMemo(() => historyOf(analyses ?? []), [analyses]);

  const railDay = useMemo(() => {
    if (!snapshots.length) return null;
    return snapshots.find((s) => s.date === current?.date) ?? snapshots[0];
  }, [snapshots, current]);

  const railDayMatches = !!railDay && !!current && railDay.date === current.date;

  const rails = useMemo<OutreachRail[]>(() => {
    if (!railDay) return [];
    return Object.values(railDay.rails ?? {}).sort(
      (a, b) =>
        statusRank(a.status) - statusRank(b.status) ||
        a.channel.localeCompare(b.channel) ||
        a.account.localeCompare(b.account) ||
        a.unit.localeCompare(b.unit)
    );
  }, [railDay]);

  const openFindings = useMemo(() => {
    const list = (current?.findings ?? []).filter((f) => !isClosed(f));
    return [...list].sort(
      (a, b) =>
        (PRIORITY_RANK[a.priority] ?? 9) - (PRIORITY_RANK[b.priority] ?? 9) ||
        a.title.localeCompare(b.title)
    );
  }, [current]);

  const closedFindings = useMemo(() => {
    const list = (current?.findings ?? []).filter(isClosed);
    return [...list].sort(
      (a, b) =>
        (PRIORITY_RANK[a.priority] ?? 9) - (PRIORITY_RANK[b.priority] ?? 9) ||
        a.title.localeCompare(b.title)
    );
  }, [current]);

  const openP0 = useMemo(
    () => openFindings.filter((f) => f.priority === "P0").length,
    [openFindings]
  );

  const latestDate = analyses && analyses.length ? analyses[0].date : null;

  const setStatus = useCallback(
    async (finding: OutreachAnalysisFinding, status: string) => {
      if (!current) return;
      const generation = generationRef.current;
      const date = current.date;
      const previous = finding.status;
      setActionError(null);
      setSaving(finding.id);
      // Optimistic: the row settles at once and rolls back on a failure.
      const patch = (next: string) =>
        setAnalyses((list) =>
          (list ?? []).map((day) =>
            day.date === date
              ? {
                  ...day,
                  findings: day.findings.map((f) =>
                    f.id === finding.id ? { ...f, status: next } : f
                  ),
                }
              : day
          )
        );
      patch(status);
      try {
        await updateOutreachAnalysisFinding(accountId, date, finding.id, status);
      } catch (e: any) {
        // The screen has moved to another account: its rows are not ours to
        // roll back, and its error line is not ours to write.
        if (generationRef.current !== generation) return;
        patch(previous);
        setActionError(e?.message || "Could not update that finding");
      } finally {
        setSaving(null);
      }
    },
    [accountId, current]
  );

  return {
    analyses,
    snapshots,
    current,
    error,
    actionError,
    saving,
    selectedDate,
    setSelectedDate,
    history,
    rails,
    railDay,
    railDayMatches,
    isLatestDay: !!current && current.date === latestDate,
    latestDate,
    openFindings,
    closedFindings,
    openP0,
    setStatus,
    reload: load,
  };
};
